import React, { useState, useEffect } from 'react';
import { Target, TrendingUp, DollarSign, Home, TrendingDown, CheckCircle, Circle, Calendar, ChevronDown, ChevronUp, ClipboardList } from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'sonner';
import RenewalTimeline from '../RenewalTimeline';
import OffersManager from '../OffersManager';

const RenewalPlannerTab = ({ mortgage, marketRates }) => {
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [items, setItems] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState(true);
  const [isProgressExpanded, setIsProgressExpanded] = useState(true);
  
  const goals = [
    {
      id: 'payoff',
      name: 'Pay Off Early',
      description: 'Eliminate your mortgage debt ahead of schedule',
      icon: Home,
      color: 'text-blue-400'
    },
    {
      id: 'reduce_interest',
      name: 'Minimize Interest',
      description: 'Reduce total interest paid over the loan life',
      icon: TrendingDown,
      color: 'text-green-400'
    },
    {
      id: 'minimize_payment',
      name: 'Minimize Payments',
      description: 'Keep monthly mortgage payments as low as possible given market conditions',
      icon: DollarSign,
      color: 'text-purple-400'
    },
    {
      id: 'refinance',
      name: 'Refinance Better',
      description: 'Secure the best available rate through strategic refinancing',
      icon: TrendingUp,
      color: 'text-orange-400'
    }
  ];
  
  // Default checklist items
  const defaultItems = [
    { id: 1, text: 'Review current mortgage terms', completed: false, category: '3-4months' },
    { id: 2, text: 'Check credit score', completed: false, category: '3-4months' },
    { id: 3, text: 'Gather financial documents', completed: false, category: '3-4months' },
    { id: 4, text: 'Monitor market rates', completed: false, category: '3-4months' },
    { id: 5, text: 'Contact mortgage brokers', completed: false, category: '3-4months' },
    { id: 6, text: 'Get pre-approval from 2-3 lenders', completed: false, category: '3-4months' },
    { id: 7, text: 'Compare final offers', completed: false, category: '1-2months' },
    { id: 8, text: 'Negotiate with current lender', completed: false, category: '1-2months' },
    { id: 9, text: 'Consider HELOC options', completed: false, category: '1-2months' },
    { id: 10, text: 'Review all fees and costs', completed: false, category: '1-2months' },
    { id: 11, text: 'Sign renewal documents', completed: false, category: 'final' },
    { id: 12, text: 'Set up new payment schedule', completed: false, category: 'final' },
    { id: 13, text: 'Update insurance arrangements', completed: false, category: 'final' },
  ];
  
  useEffect(() => {
    loadProgress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const loadProgress = async () => {
    try {
      const response = await api.get('/api/progress');
      if (response.data && response.data.length > 0) {
        setItems(response.data);
      } else {
        setItems(defaultItems);
      }
    } catch (error) {
      console.error('Error loading progress:', error);
      setItems(defaultItems);
    } finally {
      setLoadingProgress(false);
    }
  };
  
  const toggleItem = async (id) => {
    const updatedItems = items.map(item =>
      item.id === id ? { ...item, completed: !item.completed } : item
    );
    setItems(updatedItems);
    
    try {
      await api.post('/api/progress', updatedItems);
    } catch (error) {
      console.error('Error saving progress:', error);
      toast.error('Failed to save progress');
    }
  };
  
  const completedCount = items.filter(item => item.completed).length;
  const progress = items.length > 0 ? (completedCount / items.length) * 100 : 0;
  
  const itemsByCategory = {
    '3-4months': items.filter(item => item.category === '3-4months'),
    '1-2months': items.filter(item => item.category === '1-2months'),
    'final': items.filter(item => item.category === 'final')
  };
  
  const calculateGoalMetrics = (goalId) => {
    if (!mortgage.current_balance || !mortgage.current_rate) return null;
    
    const bestRate = marketRates.rates && marketRates.rates.length > 0
      ? Math.min(...marketRates.rates.map(r => r.fixed_5y))
      : mortgage.current_rate;
    
    switch (goalId) {
      case 'payoff':
        return {
          current: `${mortgage.amortization_remain || 25} years remaining`,
          target: `Pay off in ${Math.max(10, (mortgage.amortization_remain || 25) - 5)} years`,
          action: 'Increase payments by 15-20%'
        };
      
      case 'reduce_interest':
        const totalInterest = mortgage.current_balance * (mortgage.current_rate / 100) * (mortgage.amortization_remain || 25) * 0.6;
        return {
          current: `$${totalInterest.toLocaleString('en-CA', {maximumFractionDigits: 0})} projected`,
          target: `Save $${(totalInterest * 0.2).toLocaleString('en-CA', {maximumFractionDigits: 0})}+`,
          action: 'Make bi-weekly payments'
        };
      
      case 'lower_payment':
        const monthlyPayment = (mortgage.current_balance * (mortgage.current_rate / 100 / 12) * Math.pow(1 + mortgage.current_rate / 100 / 12, (mortgage.amortization_remain || 25) * 12)) / (Math.pow(1 + mortgage.current_rate / 100 / 12, (mortgage.amortization_remain || 25) * 12) - 1);
        return {
          current: `$${monthlyPayment.toLocaleString('en-CA', {maximumFractionDigits: 0})}/month`,
          target: `Reduce to $${(monthlyPayment * 0.85).toLocaleString('en-CA', {maximumFractionDigits: 0})}/month`,
          action: 'Refinance to lower rate'
        };
      
      case 'refinance':
        return {
          current: `${mortgage.current_rate}% current rate`,
          target: `${bestRate.toFixed(2)}% market rate`,
          action: `Save ${(mortgage.current_rate - bestRate).toFixed(2)}% APR`
        };
      
      default:
        return null;
    }
  };
  
  const renderProgressChecklist = (category, title, icon) => {
    const Icon = icon;
    const categoryItems = itemsByCategory[category];
    const categoryCompleted = categoryItems.filter(i => i.completed).length;
    
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Icon className="h-4 w-4 text-primary" />
            {title}
          </h4>
          <span className="text-xs text-muted-foreground">{categoryCompleted}/{categoryItems.length}</span>
        </div>
        <div className="space-y-1">
          {categoryItems.map(item => (
            <div
              key={item.id}
              className="flex items-center gap-2 p-2 bg-background rounded-lg hover:bg-muted/30 transition-colors"
            >
              <button
                onClick={() => toggleItem(item.id)}
                className="flex-shrink-0"
              >
                {item.completed ? (
                  <CheckCircle className="h-4 w-4 text-primary" data-testid={`task-${item.id}-completed`} />
                ) : (
                  <Circle className="h-4 w-4 text-muted-foreground" data-testid={`task-${item.id}-incomplete`} />
                )}
              </button>
              <span className={`text-sm flex-1 ${item.completed ? 'line-through text-muted-foreground' : ''}`}>
                {item.text}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };
  
  return (
    <div data-testid="renewal-planner-tab" className="space-y-6">
      <div>
        <h2 className="text-2xl font-heading font-semibold flex items-center gap-2">
          <ClipboardList className="h-6 w-6 text-primary" />
          Renewal Planner
        </h2>
        <p className="text-muted-foreground text-sm mt-1">Plan your renewal journey with timelines, goals, and progress tracking</p>
      </div>
      
      {/* Renewal Timeline */}
      <RenewalTimeline mortgage={mortgage} />
      
      {/* Bank Offers Manager */}
      <OffersManager mortgage={mortgage} />
      
      {/* Progress Tracker - Collapsible */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        <button
          onClick={() => setIsProgressExpanded(!isProgressExpanded)}
          data-testid="toggle-progress-btn"
          className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
        >
          <div className="flex items-center gap-4">
            <div className="h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-primary" />
            </div>
            <div className="text-left">
              <h3 className="text-lg font-heading font-semibold">Renewal Progress</h3>
              <p className="text-sm text-muted-foreground">
                {completedCount} of {items.length} tasks completed
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <p className="text-2xl font-bold text-primary">{progress.toFixed(0)}%</p>
            </div>
            {isProgressExpanded ? (
              <ChevronUp className="h-5 w-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="h-5 w-5 text-muted-foreground" />
            )}
          </div>
        </button>
        
        {/* Progress Bar */}
        <div className="px-6 pb-2">
          <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
            <div
              className="bg-primary h-full transition-all duration-500 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
        
        {/* Progress Content */}
        <div className={`transition-all duration-300 ease-in-out ${isProgressExpanded ? 'max-h-[2000px]' : 'max-h-0 overflow-hidden'}`}>
          {loadingProgress ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="p-6 pt-4 border-t border-border grid grid-cols-1 md:grid-cols-3 gap-6">
              {renderProgressChecklist('3-4months', '3-4 Months Before', Calendar)}
              {renderProgressChecklist('1-2months', '1-2 Months Before', Calendar)}
              {renderProgressChecklist('final', 'Final Steps', CheckCircle)}
            </div>
          )}
        </div>
      </div>
      
      {/* Goal Selection */}
      <div>
        <h3 className="text-lg font-heading font-semibold mb-4">Your Mortgage Goals</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {goals.map(goal => {
            const Icon = goal.icon;
            const metrics = calculateGoalMetrics(goal.id);
            const isSelected = selectedGoal === goal.id;
            
            return (
              <div
                key={goal.id}
                onClick={() => setSelectedGoal(isSelected ? null : goal.id)}
                className={`bg-card border rounded-xl p-5 cursor-pointer transition-all ${
                  isSelected
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-border hover:border-primary/50'
                }`}
              >
                <div className="flex items-start gap-3">
                  <div className={`h-10 w-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`h-5 w-5 ${goal.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-heading font-semibold mb-1">{goal.name}</h4>
                    <p className="text-sm text-muted-foreground">{goal.description}</p>
                    
                    {isSelected && metrics && (
                      <div className="mt-3 pt-3 border-t border-border space-y-1.5 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Current:</span>
                          <span className="font-medium">{metrics.current}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Target:</span>
                          <span className="font-medium text-primary">{metrics.target}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Action:</span>
                          <span className="font-medium">{metrics.action}</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Goal Strategies */}
      {selectedGoal && (
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="text-lg font-heading font-semibold mb-4">Recommended Strategies</h3>
          
          {selectedGoal === 'payoff' && (
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Switch to bi-weekly payments (equivalent to 13 monthly payments per year)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Make annual lump sum prepayments (most mortgages allow 10-20%)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Round up payments to the nearest $100 or $500</span>
              </li>
            </ul>
          )}
          
          {selectedGoal === 'reduce_interest' && (
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Refinance to a lower interest rate when rates drop</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Make additional principal payments early in the term</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Choose shorter amortization period if cash flow allows</span>
              </li>
            </ul>
          )}
          
          {selectedGoal === 'minimize_payment' && (
            <ul className="space-y-2 text-sm">
              {mortgage.current_rate && marketRates.rates && marketRates.rates.length > 0 && 
               Math.min(...marketRates.rates.map(r => r.fixed_5y)) > mortgage.current_rate ? (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span><strong>Facing higher rates?</strong> Extend amortization period to spread payments over more time</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Consider variable rate if you can handle short-term fluctuations (often lower initially)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Negotiate with your current lender for retention discounts before switching</span>
                  </li>
                </>
              ) : (
                <>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Refinance to extend your amortization period and reduce monthly obligations</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Shop for lower interest rates from multiple lenders to reduce payment amounts</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-primary mt-0.5">•</span>
                    <span>Consider variable rate if you can handle payment fluctuations for lower initial rates</span>
                  </li>
                </>
              )}
            </ul>
          )}
          
          {selectedGoal === 'refinance' && (
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Compare rates from at least 3 different lenders</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Calculate break-even point considering refinancing costs</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary mt-0.5">•</span>
                <span>Time your refinance when rates are at least 0.5% lower</span>
              </li>
            </ul>
          )}
        </div>
      )}
      
      {!mortgage.current_balance && (
        <div className="bg-card border border-border rounded-xl p-8 text-center">
          <Target className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <h3 className="text-lg font-semibold mb-2">Set Your Goals</h3>
          <p className="text-muted-foreground text-sm">
            Enter your mortgage details first to see personalized goal recommendations.
          </p>
        </div>
      )}
    </div>
  );
};

export default RenewalPlannerTab;
